const UPDATE_CHECK_INTERVAL = 30 * 60 * 1000;
const LAST_UPDATE_CHECK_KEY = "easyhmo:last-service-worker-update-check";
const VERSION_REQUEST = "easyhmo-version-request";
const UPDATE_READY_EVENT = "easyhmo-app-update-ready";

let serviceWorkerRegistration = null;
let updateCheck = null;
let lastUpdateCheck = 0;
const observedWorkers = new WeakSet();
const announcedWorkers = new WeakSet();

const readLastUpdateCheck = () => {
  try {
    const storedValue = Number(window.localStorage.getItem(LAST_UPDATE_CHECK_KEY));
    return Number.isFinite(storedValue) ? Math.max(lastUpdateCheck, storedValue) : lastUpdateCheck;
  } catch {
    return lastUpdateCheck;
  }
};

const recordUpdateCheck = () => {
  lastUpdateCheck = Date.now();

  try {
    window.localStorage.setItem(LAST_UPDATE_CHECK_KEY, String(lastUpdateCheck));
  } catch {
    // The in-memory timestamp still protects this tab when storage is unavailable.
  }
};

const requestWorkerVersion = (worker) => new Promise((resolve) => {
  if (!worker || typeof MessageChannel === "undefined") {
    resolve({});
    return;
  }

  const channel = new MessageChannel();
  const timeout = window.setTimeout(() => resolve({}), 1500);

  channel.port1.onmessage = (event) => {
    window.clearTimeout(timeout);
    resolve(event.data || {});
  };

  try {
    worker.postMessage({ type: VERSION_REQUEST }, [channel.port2]);
  } catch {
    window.clearTimeout(timeout);
    resolve({});
  }
});

const announceUpdate = async (worker) => {
  if (!worker || announcedWorkers.has(worker)) {
    return;
  }

  announcedWorkers.add(worker);
  const version = await requestWorkerVersion(worker);
  window.dispatchEvent(new CustomEvent(UPDATE_READY_EVENT, { detail: version }));
};

const observeWorker = (worker) => {
  if (!worker || observedWorkers.has(worker)) {
    return;
  }

  observedWorkers.add(worker);

  const handleStateChange = () => {
    if (worker.state === "installed" && navigator.serviceWorker.controller) {
      announceUpdate(worker);
    }
  };

  worker.addEventListener("statechange", handleStateChange);
  handleStateChange();
};

const checkForUpdate = () => {
  if (
    !serviceWorkerRegistration ||
    updateCheck ||
    navigator.onLine === false ||
    Date.now() - readLastUpdateCheck() < UPDATE_CHECK_INTERVAL
  ) {
    return updateCheck || Promise.resolve(false);
  }

  recordUpdateCheck();
  updateCheck = serviceWorkerRegistration.update()
    .catch(() => false)
    .finally(() => {
      updateCheck = null;
    });

  return updateCheck;
};

const getRouteComponent = (route) => {
  const matched = route?.matched || [];
  return matched[matched.length - 1]?.components?.default || null;
};

const registerNavigationChecks = (router) => {
  if (!router?.afterEach) {
    return;
  }

  router.afterEach((to, from, failure) => {
    const fromComponent = getRouteComponent(from);
    const toComponent = getRouteComponent(to);

    if (!failure && fromComponent && fromComponent !== toComponent) {
      checkForUpdate();
    }
  });
};

const registerVisibilityChecks = () => {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkForUpdate();
    }
  });
};

const registerServiceWorker = (router) => {
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) {
    return;
  }

  registerNavigationChecks(router);
  registerVisibilityChecks();

  window.addEventListener("load", async () => {
    try {
      serviceWorkerRegistration = await navigator.serviceWorker.register(
        `${process.env.BASE_URL}service-worker.js`
      );
      recordUpdateCheck();

      serviceWorkerRegistration.addEventListener("updatefound", () => {
        observeWorker(serviceWorkerRegistration.installing);
      });

      observeWorker(serviceWorkerRegistration.installing);
      observeWorker(serviceWorkerRegistration.waiting);
    } catch {
      // Installation still works as a normal web app if registration fails.
    }
  });
};

export default registerServiceWorker;
